<template>
  <BaseModal
    :modelValue="modelValue"
    :size="'full'"
    @update:modelValue="$emit('update:modelValue', $event)"
    @cancel="$emit('update:modelValue', false)"
  >
    <template v-slot:title>
      {{ $t('Projects.New project popup.Compare_goal_collection_title') }}
    </template>

    <!-- Framework Picker -->
    <div v-if="projectFrameworks.length > 1" class="px-4 py-4 border-b border-gray-200 bg-gray-50">
      <div class="flex items-center gap-4">
        <label class="text-sm font-medium text-gray-700">{{ $t('Projects.New project popup.Compare_goal_collection_title') }}:</label>
        <div class="flex gap-2">
          <button
            v-for="frameworkId in projectFrameworks"
            :key="frameworkId"
            @click="selectFramework(frameworkId)"
            class="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 shadow-sm"
            :class="{
              'bg-blue-600 text-white shadow-md ring-2 ring-blue-200': selectedFrameworkId === frameworkId,
              'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border border-gray-200': selectedFrameworkId !== frameworkId
            }"
          >
            {{ getFrameworkName(frameworkId) }}
          </button>
        </div>
        <div class="ml-auto text-xs text-gray-500">
          {{ $t('Projects.New project popup.Compare_goal_collection_description') }}
        </div>
      </div>
    </div>

    <!-- Collection Picker -->
    <div
      v-if="selectedFrameworkCollections.length > 0 || isLoadingCollectionsData"
      class="grid grid-cols-1 md:grid-cols-12 py-4 divide-x" style="grid-template-columns: fit-content(300px) 1fr;"
    >
      <!-- Collections List (Left Side) -->
      <div class="flex flex-col gap-4 pr-6">
        <!-- Loading State for Collections -->
        <div v-if="isLoadingCollectionsData" class="flex flex-col gap-4">
          <div
            v-for="n in 3"
            :key="`collection-skeleton-${n}`"
            class="flex flex-col rounded-md border-2 border-transparent px-4 py-2 animate-pulse"
          >
            <!-- Collection Title Skeleton -->
            <div class="flex flex-col justify-start items-center mb-4">
              <div class="h-5 w-24 bg-gray-300 rounded"></div>
            </div>
            <!-- Rose Chart Skeleton -->
            <div class="w-full mb-6 flex justify-center">
              <div class="w-48 h-48 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </div>

        <!-- Actual Collections -->
        <div
          v-else
          v-for="(collection, index) in selectedFrameworkCollections"
          :key="index"
          class="flex flex-col hover:bg-gray-200 cursor-pointer rounded-md border-2 px-4 py-2 relative"
          :class="selectedCollectionIdLocal === collection.id ? 'border-blue-500' : 'border-transparent'"
        >
          <div class="flex flex-col justify-start items-center mb-4">
            <span class="text-xs px-1 py-0.5 rounded text-white" :class="`bg-${getCollectionColor(collection.id)}`">
              {{ collection.title }}
            </span>
          </div>
          <div class="w-full mb-6">
            <RoseChartD3
              :chartId="`compare-collection-${collection.id}`"
              :categories="categories"
              :datasets="collection.datasets"
              :options="{
                canSelectCategory: false,
                canSaveRatings: false,
                clickable: false,
                canHover: false,
                maxValue: collection.maxValue || 100,
                steps: collection.steps || 5,
                fullHeight: true,
                chartWidth: 200,
                chartHeight: 200
              }"
            />
          </div>
          <div class="absolute inset-0 opacity-0 rounded-lg" @click.stop="selectCollection(collection.id)"></div>
        </div>
      </div>
      <!-- Right Side Content -->
      <div class="space-y-3 h-full pl-6" id="matrix-container" data-cy="workshopItemsContainer">
        <!-- Loading State for Categories and SubCategories -->
        <div v-if="isLoadingSubCategoriesData" class="animate-pulse">
          <div v-for="n in 4" :key="`category-skeleton-${n}`" class="flex gap-4 mb-6">
            <!-- Category Icon Skeleton -->
            <div class="cursor-pointer my-auto mr-3-em p-3-em h-20-em w-32-em flex-shrink-0 flex flex-col items-center space-y-1 relative">
              <div class="h-10-em w-10-em bg-gray-300 rounded"></div>
              <div class="h-3 w-16 bg-gray-300 rounded"></div>
              <!-- Theme Score Skeleton -->
              <div class="absolute top-0 right-0 w-6 h-6 bg-gray-300 rounded-full"></div>
            </div>
            <!-- SubCategory Cards Skeleton -->
            <div v-for="cardN in 2" :key="`subcategory-skeleton-${n}-${cardN}`" class="w-64">
              <div class="bg-white card p-4 animate-pulse">
                <!-- Title Skeleton -->
                <div class="h-6 bg-gray-300 rounded mb-2"></div>
                <!-- Description Skeleton -->
                <div class="space-y-2 mb-4">
                  <div class="h-4 bg-gray-200 rounded w-full"></div>
                  <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
                <!-- Rating Tags Skeleton -->
                <div class="flex gap-2 pt-3">
                  <div class="h-6 w-8 bg-gray-300 rounded"></div>
                  <div class="h-6 w-8 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actual Content -->
        <div v-else-if="selectedCollectionIdLocal" class="flex flex-col gap-4">
          <div v-for="(category, index) in categories" :key="category.id" class="flex gap-4">
            <div
              class="cursor-pointer my-auto mr-3-em p-3-em h-20-em w-32-em flex-shrink-0 flex flex-col items-center space-y-1 relative"
              @click="$emit('openTheme', category.id)"
            >
              <div class="h-10-em">
                <img :src="category.iconUrl" :alt="category.title" class="w-full h-full">
              </div>
              <h4 class="text-xs-em text-center" :style="`color: ${category.color};`">{{category.title}}</h4>
              <div class="absolute top-0 right-0">
                <ThemeScoreDum
                  :datasets="[selectedCollection?.datasets?.[0]?.values?.[index] || 0, 0]"
                  :data-sources="[{ id: 'goal-ratings-avg:4', label: 'Goal Ratings Avg' }]"
                  size="sm"
                />
              </div>
            </div>
            <SubCategoryGoalCard
              v-for="subCategory in subCategoriesWithGoals.filter(subCategory => subCategory.categoryId === category.id).sort(a => subCatIsEnabled(a) ? -1 : 1)"
              :key="subCategory.id"
              :id="subCategory.id"
              :subCategoryId="subCategory.id"
              :title="getSubCategoryTitle(subCategory)"
              :subCategoryDescription="subCategory.description || ''"
              :goalDescription="''"
              :selectedCollectionId="selectedCollectionIdLocal"
              :showMultipleRatings="true"
              :goalCollectionsSubCategories="subCategory.goals"
              :isEnabled="subCatIsEnabled(subCategory)"
              :canEdit="false"
              :canDelete="false"
              class="w-64"
            ></SubCategoryGoalCard>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-8">
      <p class="text-gray-500">{{ $t('Projects.New project popup.No_goal_collections_available') }}</p>
    </div>

    <template v-slot:actions>
      <div class="flex justify-end gap-4">
        <BaseButton
          :layout="'secondary'"
          :label="$t('Shared.cancel')"
          @click="$emit('update:modelValue', false)"
        ></BaseButton>
        <BaseButton
          :layout="'primary'"
          :label="$t('Shared.save')"
          @click="saveSelection"
        ></BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/shared/BaseModal'
import BaseButton from '@/components/shared/BaseButton'
import RoseChartD3 from '@/components/shared/RoseChartD3.vue'
import SubCategoryGoalCard from '@/components/shared/SubCategoryGoalCard'
import ThemeScoreDum from '@/components/workshop/ThemeScoreDum.vue'
import getSubCategoriesWithGoals from '@/composables/goals/getSubCategoriesWithGoals'
import { getCollectionColor } from '@/composables/goalCollections/utils'
export default {
  name: 'ModalCompareGoalCollections',
  components: {
    BaseModal,
    BaseButton,
    RoseChartD3,
    SubCategoryGoalCard,
    ThemeScoreDum,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    collections: {
      type: Array,
      default: () => [],
    },
    selectedCollectionId: {
      type: [Number, String],
      default: null,
    },
    projectFrameworks: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'select', 'save'],
  data () {
    return {
      matrixTopPos: 0,
      showActions: true,
      selectedFrameworkId: null,
      selectedCollectionIdLocal: null,
      goalCollections: [],
      cachedSubCategoriesWithGoals: {},
      subCategoriesWithGoals: [],
      dataValues: [],
      isLoading: true,
      isLoadingCollectionsData: true,
      isLoadingSubCategoriesData: true,
    }
  },
  watch: {
    modelValue: {
      async handler (newVal) {
        if (!newVal) return
        // Reset loading states
        this.isLoadingCollectionsData = true
        this.isLoadingSubCategoriesData = true
        this.cachedSubCategoriesWithGoals = {}

        // Initialize framework selection
        if (this.projectFrameworks.length > 0) {
          await this.selectFramework(this.projectFrameworks[0])
        }

        // Load subcategories for the selected framework
        if (this.selectedFrameworkId) {
          this.subCategoriesWithGoals = await this.loadCachedSubCategoriesWithGoals(this.selectedFrameworkId)
        }
        console.log('!!ModalCompareGoalCollections opened', this.selectedFrameworkId, this.projectFrameworks, this.collections)

        this.isLoadingCollectionsData = false
      },
    },
    selectedFrameworkId: {
      async handler (newVal) {
        if (!this.modelValue || !newVal) return
        this.isLoadingSubCategoriesData = true
        this.subCategoriesWithGoals = await this.loadCachedSubCategoriesWithGoals(newVal)
      },
    },
    selectedCollectionId: {
      async handler (newVal) {
        this.selectedCollectionIdLocal = newVal
      },
      immediate: true,
    },
  },
  computed: {
    categories () {
      if (!this.selectedFrameworkId) return []
      return this.$store.getters.frameworkCategories(this.selectedFrameworkId)
    },
    uniqueFrameworkIds () {
      return [...new Set(this.collections.map(collection => collection.frameworkId))]
    },
    selectedFrameworkCollections () {
      return this.collections.filter(collection => collection.frameworkId === this.selectedFrameworkId)
    },
    selectedCollection () {
      return this.collections.find(collection => collection.id === this.selectedCollectionIdLocal)
    },
  },
  methods: {
    getCollectionColor (collectionId) {
      return getCollectionColor(collectionId)
    },
    subCatIsEnabled (subCategory) {
      const currentGoal = subCategory.goals.find(goal => goal.goalCollectionId === this.selectedCollectionIdLocal)
      if (currentGoal) {
        return currentGoal.isEnabled
      }
      return false
    },
    async selectFramework (frameworkId) {
      if (!frameworkId) return

      this.selectedFrameworkId = frameworkId

      // if selectedCollectionId is present and the colleciton is in selectedFrameworkCollections, use it
      if (this.selectedCollectionId && this.selectedFrameworkCollections.find(c => c.id === this.selectedCollectionId)) {
        this.selectedCollectionIdLocal = this.selectedCollectionId
      } else {
        // Reset selected collection to first available in this framework
        if (this.selectedFrameworkCollections.length > 0) {
          this.selectedCollectionIdLocal = this.selectedFrameworkCollections[0].id
        }
      }
    },
    selectCollection (collectionId) {
      this.selectedCollectionIdLocal = collectionId
    },
    saveSelection () {
      this.$emit('select', this.selectedCollectionIdLocal)
    },
    getFrameworkName (frameworkId) {
      const framework = this.$store.getters['frameworks/list'].find(f => f.id === frameworkId)
      return framework ? framework.title : 'Framework'
    },
    getRatingColorClass (rating) {
      const colorClasses = {
        1: 'bg-red-100 text-red-800',
        2: 'bg-orange-100 text-orange-800',
        3: 'bg-yellow-100 text-yellow-800',
        4: 'bg-green-100 text-green-800',
        5: 'bg-emerald-100 text-emerald-800',
      }
      return colorClasses[rating] || 'bg-gray-100 text-gray-800'
    },
    async loadCachedSubCategoriesWithGoals (frameworkId) {
      this.isLoadingSubCategoriesData = true
      if (this.cachedSubCategoriesWithGoals[frameworkId]) return this.cachedSubCategoriesWithGoals[frameworkId]
      this.cachedSubCategoriesWithGoals[frameworkId] = await getSubCategoriesWithGoals(frameworkId)
      this.isLoadingSubCategoriesData = false
      return this.cachedSubCategoriesWithGoals[frameworkId]
    },
    getSubCategoryTitle (subCategory) {
      if (!subCategory) return ''
      // Try title directly first
      if (subCategory.title) return subCategory.title
      // Try defaultTranslation
      if (subCategory.defaultTranslation?.title) return subCategory.defaultTranslation.title
      // Fallback: try to get from store
      const storeSubCategory = this.$store.getters['sub-categories/list'].find(sc => sc.id === subCategory.id)
      if (storeSubCategory) {
        if (storeSubCategory.title) return storeSubCategory.title
        if (storeSubCategory.defaultTranslation?.title) return storeSubCategory.defaultTranslation.title
      }
      // Last resort fallback
      return 'Unknown Subcategory'
    },
  },
}
</script>
